import { first } from "rxjs/operators";
import { pathHead } from "@/store/helpers";
import * as action from "./action.type";
import * as mutation from "./mutation.type";
import * as getter from "./getter.type";

// TODO: Finish commenting all of the functions with JSDoc

/**
 * Option object for action `CACHE`.
 * @typedef {Object} CacheOptions
 * @property {boolean=} watch      - Setup a watcher if one is not already present.
 * @property {boolean=} forceWatch - Setup a watcher and replace the current one.
 * @property {boolean=} forceFetch - Force a new fetch of the data. (watch takes precedence)
 */

/**
 * Gets some data out of the store. This data may be already fetched in cache or
 * it will fetch it on-demand. This function also allows a `path` parameter that
 * makes it possible to query submodules through the `GET_FROM_SUBMODULES` action.
 * 
 * Required actions:
 *  - CACHE
 *  - GET_FROM_MODULES
 * 
 * Required getters:
 *  - CACHED
 * 
 * @param {*} action_param 
 * @param {Object} payload 
 * @param {CacheOptions=} payload.options - Options passed to `CACHE`
 * @param {String=} payload.path - The path some submodules
 */
export async function get({ dispatch, getters }, payload) {
  await dispatch(action.CACHE, payload?.options);
  if (pathHead(payload?.path))
    return await dispatch(action.GET_FROM_MODULES, payload);
  else
    return getters[getter.CACHED];
}

/**
 * Makes sure that there is cached data in the store. Options may specify if a
 * watcher should be setup, or if the local cache should force an update.
 * 
 * Required actions:
 *  - WATCH
 *  - FETCH
 * 
 * Required getters:
 *  - IS_WATCHING
 *  - STORED
 * 
 * @param {*} action_param
 * @param {CacheOptions}  options
 */
export async function cache({ dispatch, getters }, options) {
  if ((options?.watch && !getters[getter.IS_WATCHING]) || options?.forceWatch)
    await dispatch(action.WATCH);
  else if (!getters[getter.STORED] || options?.forceFetch)
    await dispatch(action.FETCH);
}

/**
 * Dispatches `FETCH_DOCUMENT` and commits `SET_FETCH_TIMESTAMP` afterwards.
 * 
 * Required actions:
 *  - FETCH_DOCUMENT
 * 
 * Required mutations:
 *  - SET_FETCH_TIMESTAMP
 * 
 * @param {*} action_param
 * @throws Will throw an error in case the document fetch is unsuccessfull.
 */
export async function fetch({ dispatch, commit }) {
  try {
    await dispatch(action.FETCH_DOCUMENT);
    commit(mutation.SET_FETCH_TIMESTAMP);
  } catch (error) {
    console.error(error);
    throw new Error(`${action.FETCH_DOCUMENT} action failed.`);
  }
}

/**
 * Sets up a watcher and updates the fetch timestamp.
 * 
 * Required actions:
 *  - WATCH_DOCUMENT
 * 
 * Required mutations:
 *  - SET_FETCH_TIMESTAMP
 * 
 * @param {*} action_param
 * @throws Will throw an error if the dispatch of `WATCH_DOCUMENT` fails.
 */
export async function watch({ dispatch, commit }) {
  try {
    await dispatch(action.WATCH_DOCUMENT);
    commit(mutation.SET_FETCH_TIMESTAMP);
  } catch (error) {
    console.error(error);
    throw Error("An error occurred while watching the context doc and/or the items of the context.");
  }
}

/**
 * Fetches a single document from the database pointed by the `REF` getter. After
 * the fetch, commits `SET_DOCUMENT` with the payload.
 * 
 * Required mutations:
 *  - SET_DOCUMENT
 * 
 * Required getters:
 *  - REF
 * 
 * @param {*} action_param
 * @throws Will throw an error if the document fails to get.
 */
export async function fetch_document({ commit, getters }) {
  try {
    const result = await getters[getter.REF].get();
    commit(mutation.SET_DOCUMENT, result);
    return result;
  } catch (error) {
    console.error(error);
    throw new Error("An error occurred while fetching the context document from the database.");
  }
}

/**
 * Sets up a document watcher for the document pointed by the `REF` getter. If
 * a listener is already inplace, that listener is detached.
 * 
 * Required mutations:
 *  - SET_DOCUMENT
 *  - SET_DOCUMENT_LISTENER
 * 
 * @param {*} action_param
 */
export async function watch_document({ dispatch, commit, getters }) {
  // Detaches the document listener if there is one already setup.
  getters[getter.DOCUMENT_LISTENER]?.complete();

  // Attaches a document listener to the store.
  const subject = await dispatch(action.ATTATCH_LISTENER, {
    ref: getters[getter.REF],
    mutation: snapshot => commit(mutation.SET_DOCUMENT, snapshot)
  }, { root: true });
  commit(mutation.SET_DOCUMENT_LISTENER, subject);
  return await subject.pipe(first()).toPromise();
}